import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  HOBBLI_SUPPORT_ID,
  formatAdminDate,
  formatCompactNumber,
} from "./adminConfig";

const EMPTY_STATS = {
  totalUsers: 0,
  newUsers: 0,
  totalItems: 0,
  totalUserItems: 0,
  unlinkedUserItems: 0,
  pendingVariantSuggestions: 0,
  unreadMessages: 0,
};

export default function AdminOverview({ onNavigate, onCountsRefresh }) {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [recentItems, setRecentItems] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const twentyDaysAgo = new Date();
    twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);

    const results = await Promise.all([
      supabase.from("users").select("id", { count: "exact", head: true }),
      supabase
        .from("users")
        .select("id", { count: "exact", head: true })
        .gte("created_at", twentyDaysAgo.toISOString()),
      supabase.from("items").select("id", { count: "exact", head: true }),
      supabase.from("user_items").select("id", { count: "exact", head: true }),
      supabase
        .from("user_items")
        .select("id", { count: "exact", head: true })
        .is("item_id", null),
      supabase
        .from("user_variant_suggestions")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("receiver_id", HOBBLI_SUPPORT_ID)
        .is("read_at", null),
      supabase
        .from("items")
        .select("id, name, brand, set_name, year, created_at")
        .order("created_at", { ascending: false })
        .limit(6),
      supabase
        .from("users")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(6),
      supabase
        .from("messages")
        .select("*")
        .eq("receiver_id", HOBBLI_SUPPORT_ID)
        .order("created_at", { ascending: false })
        .limit(5),
    ]);

    const [
      usersCount,
      newUsersCount,
      itemsCount,
      userItemsCount,
      unlinkedCount,
      variantSuggestionCount,
      unreadCount,
      recentItemsResult,
      recentUsersResult,
      recentMessagesResult,
    ] = results;

    const hardErrors = results
      .slice(0, 7)
      .map((result) => result.error)
      .filter(Boolean);

    if (hardErrors.length) {
      console.warn("Some admin overview queries failed:", hardErrors);
      setErrorMessage(
        "Some dashboard totals could not be loaded. Check the console for the Supabase error."
      );
    }

    setStats({
      totalUsers: usersCount.count || 0,
      newUsers: newUsersCount.count || 0,
      totalItems: itemsCount.count || 0,
      totalUserItems: userItemsCount.count || 0,
      unlinkedUserItems: unlinkedCount.count || 0,
      pendingVariantSuggestions: variantSuggestionCount.count || 0,
      unreadMessages: unreadCount.count || 0,
    });

    setRecentItems(recentItemsResult.data || []);
    setRecentUsers(recentUsersResult.data || []);
    setRecentMessages(recentMessagesResult.data || []);
    setLoading(false);

    onCountsRefresh?.();
  }, [onCountsRefresh]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const statCards = [
    {
      label: "Canonical Items",
      value: stats.totalItems,
      note: "Shared catalog records",
      section: "catalog",
    },
    {
      label: "User Items",
      value: stats.totalUserItems,
      note: `${formatCompactNumber(stats.unlinkedUserItems)} awaiting canonical linkage`,
      section: "itemReview",
    },
    {
      label: "Item Review",
      value: stats.unlinkedUserItems,
      note: "User items with no canonical item_id",
      section: "itemReview",
    },
    {
      label: "Variant Review",
      value: stats.pendingVariantSuggestions,
      note: "Pending user variant suggestions",
      section: "variantReview",
    },
    {
      label: "Unread Messages",
      value: stats.unreadMessages,
      note: "Support inbox",
      section: "messages",
    },
    {
      label: "Total Users",
      value: stats.totalUsers,
      note: `${formatCompactNumber(stats.newUsers)} joined in the last 20 days`,
      section: "users",
    },
  ];

  return (
    <div className="hadmin-overview">
      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}

      <div className="hadmin-grid">
        {statCards.map((card) => (
          <button
            type="button"
            className="hadmin-stat-card"
            key={card.label}
            onClick={() => onNavigate?.(card.section)}
          >
            <div className="hadmin-stat-label">{card.label}</div>
            <div className="hadmin-stat-value">
              {loading ? "…" : formatCompactNumber(card.value)}
            </div>
            <div className="hadmin-stat-note">{card.note}</div>
          </button>
        ))}
      </div>

      <div className="hadmin-toolbar" style={{ marginTop: 18 }}>
        <button className="hadmin-btn" type="button" onClick={() => onNavigate?.("catalog")}>
          Manage Canonical Items
        </button>
        <button className="hadmin-btn-secondary" type="button" onClick={() => onNavigate?.("itemReview")}>
          Review User Items
        </button>
        <button className="hadmin-btn-secondary" type="button" onClick={() => onNavigate?.("variantReview")}>
          Review Variants
        </button>
        <button className="hadmin-btn-secondary" type="button" onClick={() => onNavigate?.("imports")}>
          Batch Import
        </button>
        <button className="hadmin-btn-secondary" type="button" onClick={fetchOverview}>
          Refresh
        </button>
      </div>

      <div className="hadmin-grid" style={{ marginTop: 18 }}>
        <section className="hadmin-card">
          <div className="hadmin-section-heading">
            <div>
              <h3>Recently Added Catalog Items</h3>
              <p>The newest canonical records.</p>
            </div>
          </div>

          <div className="hadmin-list">
            {recentItems.length ? (
              recentItems.map((item) => (
                <button type="button" className="hadmin-list-item" key={item.id} onClick={() => onNavigate?.("catalog")}>
                  <strong>{item.name || "Unnamed item"}</strong>
                  <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 12 }}>
                    {[item.year, item.brand, item.set_name].filter(Boolean).join(" • ") || "No details"}
                  </div>
                </button>
              ))
            ) : (
              <div className="hadmin-empty">No recent catalog items.</div>
            )}
          </div>
        </section>

        <section className="hadmin-card">
          <div className="hadmin-section-heading">
            <div>
              <h3>Newest Users</h3>
              <p>Recent Hobbli account activity.</p>
            </div>
          </div>

          <div className="hadmin-list">
            {recentUsers.length ? (
              recentUsers.map((user) => (
                <button type="button" className="hadmin-list-item" key={user.id} onClick={() => onNavigate?.("users")}>
                  <strong>{user.username || user.email || "User"}</strong>
                  <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 12 }}>
                    {formatAdminDate(user.created_at)}
                  </div>
                </button>
              ))
            ) : (
              <div className="hadmin-empty">No recent users.</div>
            )}
          </div>
        </section>

        <section className="hadmin-card">
          <div className="hadmin-section-heading">
            <div>
              <h3>Recent Messages</h3>
              <p>Latest support inbox activity.</p>
            </div>
          </div>

          <div className="hadmin-list">
            {recentMessages.length ? (
              recentMessages.map((message) => (
                <button
                  type="button"
                  className={`hadmin-list-item ${!message.read_at ? "unread" : ""}`}
                  key={message.id}
                  onClick={() => onNavigate?.("messages")}
                >
                  <div className="hadmin-row-between">
                    <strong>{message.metadata?.item_name || "General Inquiry"}</strong>
                    {!message.read_at && <span className="hadmin-pill blue">Unread</span>}
                  </div>
                  <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 12 }}>
                    {message.metadata?.email || formatAdminDate(message.created_at)}
                  </div>
                </button>
              ))
            ) : (
              <div className="hadmin-empty">No support messages.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
